import { Check, LayersOutlined } from "@mui/icons-material";
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { EnvironmentLayer } from "../../app/slices/EnvironmentSlice";
import classNames from "classnames";

export default function ViewportLayerFilterControl(props: {
  layers: EnvironmentLayer[];
  selectedLayerIds: string[];
  onChange: (ids: string[]) => void;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}) {
  const { layers, selectedLayerIds, onChange, onOpenChange } = { ...props };
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const isOpen = props.isOpen ?? uncontrolledIsOpen;
  const isControlled = props.isOpen !== undefined;
  const rootRef = useRef<HTMLDivElement | null>(null);

  const defaultLayerId = useMemo(() => {
    return layers.find((layer) => layer.id === "default")?.id ?? layers[0]?.id;
  }, [layers]);

  const setOpen = useCallback(
    (nextIsOpen: boolean) => {
      onOpenChange?.(nextIsOpen);
      if (!isControlled) {
        setUncontrolledIsOpen(nextIsOpen);
      }
    },
    [isControlled, onOpenChange]
  );

  useEffect(() => {
    const onDocumentPointerDown = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDocumentPointerDown);
    document.addEventListener("keydown", onDocumentKeyDown);
    return () =>
      {
        document.removeEventListener("mousedown", onDocumentPointerDown);
        document.removeEventListener("keydown", onDocumentKeyDown);
      };
  }, [setOpen]);

  const stopEvent = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  const toggleLayer = (layerId: string) => {
    const isSelected = selectedLayerIds.includes(layerId);
    const next = isSelected
      ? selectedLayerIds.filter((id) => id !== layerId)
      : [...selectedLayerIds, layerId];

    if (next.length === 0 && defaultLayerId) {
      onChange([defaultLayerId]);
      return;
    }

    onChange(next);
  };

  const getLayerDisplayName = (layer: EnvironmentLayer) => {
    if (
      layer.id === "default" &&
      layer.name.trim().toLowerCase() === "default layer"
    ) {
      return "Default";
    }

    return layer.name;
  };

  if (layers.length === 0) return <></>;

  return (
    <div
      ref={rootRef}
      className="relative z-[102]"
      onWheel={stopEvent}
      onMouseDown={stopEvent}
      onClick={stopEvent}
    >
      <button
        className={classNames(
          "h-[30px] w-[30px] rounded-[5px]",
          "flex items-center justify-center cursor-pointer transition-colors",
          isOpen
            ? "bg-creator-primary text-creator-text hover:bg-creator-primary-hover"
            : "bg-transparent text-creator-text-sub hover:bg-creator-primary-washed hover:text-creator-primary"
        )}
        onClick={() => setOpen(!isOpen)}
      >
        <LayersOutlined fontSize="small" />
      </button>

      {isOpen && (
        <div
          className={classNames(
            "absolute bottom-[40px] left-0 min-w-[320px] max-w-[380px]",
            "max-h-[520px] overflow-y-auto rounded-[5px] border border-creator-border",
            "bg-creator-surface p-[8px] shadow-xl"
          )}
        >
          <div className="flex flex-col gap-[2px]">
            {layers.map((layer) => {
              const checked = selectedLayerIds.includes(layer.id);
              return (
                <label
                  key={layer.id}
                  className={classNames(
                    "flex items-center gap-[14px] px-[14px] py-[12px] rounded-[10px]",
                    "text-[18px] font-medium text-creator-text-sub cursor-pointer",
                    "hover:bg-creator-primary-washed"
                  )}
                  onMouseDown={stopEvent}
                  onClick={stopEvent}
                >
                  <LayersOutlined className="text-[32px] text-creator-text-sub" />
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={checked}
                    onChange={() => toggleLayer(layer.id)}
                  />
                  <span className="flex-1 truncate">
                    {getLayerDisplayName(layer)}
                  </span>
                  {checked && (
                    <Check className="text-[30px] text-creator-text-sub" />
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
